<template>
  <section class="vp-card pv-strength">
    <div class="pv-strength-head">
      <div class="pv-strength-top">
        <h6 class="pv-strength-title">{{ $t('profileCompletion.title') }}</h6>
        <span class="pv-strength-value">{{ percentage }}%</span>
      </div>
      <div class="pv-strength-track">
        <i class="pv-strength-fill" :style="{ width: `${percentage}%` }"></i>
      </div>
      <p class="pv-strength-note">{{ note }}</p>
    </div>

    <ul v-if="fields.length" class="pv-checklist">
      <li v-for="field in fields" :key="field.key" class="pv-check">
        <i
          class="ph-duotone"
          :class="field.isFilled ? 'ph-check-circle pv-check-done' : 'ph-circle-dashed pv-check-todo'"
        ></i>
        <span class="pv-check-label" :class="{ 'pv-check-label--todo': !field.isFilled }">
          {{ fieldLabel(field.key) }}
        </span>
        <button
          v-if="!field.isFilled"
          type="button"
          class="pv-check-action"
          @click="goToField(field)"
        >
          {{ $t('profileCompletion.goFill') }}
        </button>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const props = defineProps({
  completion: { type: Object, default: null }
});

const { t } = useI18n();
const router = useRouter();

const percentage = computed(() => props.completion?.completionPercentage ?? 0);

const note = computed(() =>
  props.completion?.isComplete
    ? t('profileCompletion.completeNote')
    : t('profileCompletion.visibilityMessage')
);

/**
 * Required first, and within each group the missing ones lead — the work to do sits
 * at the top while the ticked rows below are what make the list read as progress.
 */
const fields = computed(() => {
  const all = props.completion?.fields || [];
  const rank = (field) => (field.isRequired ? 0 : 1) * 2 + Number(field.isFilled);
  return [...all].sort((a, b) => rank(a) - rank(b));
});

/** Field keys come from the server; labels are resolved here so they stay translated. */
const fieldLabel = (key) => t(`profileCompletion.fields.${key}`, key);

/*
  Where each field is edited. The profile page is not one surface: contact details
  live on its Account tab, the address in the Company card, and ports/categories/
  certificates on their own screens.
*/
const SECTION_ROUTES = {
  company: { path: '/profile', query: { focus: 'company' } },
  contact: { path: '/profile', query: { focus: 'account' } },
  categories: { path: '/profile/categories' },
  ports: { path: '/profile/ports' },
  certificates: { path: '/profile/certificates' }
};

const FIELD_ROUTES = {
  companyName: { path: '/profile', query: { focus: 'account' } },
  displayName: { path: '/profile', query: { focus: 'account' } },
  about: { path: '/profile' },
  logo: { path: '/profile', query: { focus: 'logo' } },
  address: { path: '/profile', query: { focus: 'company' } },
  country: { path: '/profile', query: { focus: 'company' } }
};

const fieldRoute = (field) =>
  FIELD_ROUTES[field.key] || SECTION_ROUTES[field.section] || { path: '/profile' };

const goToField = (field) => {
  if (field.isFilled) return;

  const target = fieldRoute(field);

  // Vue Router skips a push to the identical location, so clicking the same row twice
  // would not re-trigger the page's focus handling. A changing nonce keeps each click
  // a real navigation.
  router.push({
    ...target,
    query: { ...(target.query || {}), t: Date.now() }
  });
};
</script>

<style scoped>
.pv-strength-head {
  padding: 16px 18px 14px;
}

.pv-strength-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
}

.pv-strength-title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--vp-heading-color);
}

.pv-strength-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--vp-accent);
  font-variant-numeric: tabular-nums;
}

.pv-strength-track {
  height: 8px;
  border-radius: 5px;
  background: var(--vp-body-bg);
  overflow: hidden;
  margin-top: 10px;
}

.pv-strength-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--vp-accent), var(--vp-info));
  transition: width 0.3s ease;
}

.pv-strength-note {
  margin: 8px 0 0;
  font-size: 0.78rem;
  color: var(--vp-gray-500);
}

.pv-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--vp-gray-200);
}

.pv-check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border-bottom: 1px solid var(--vp-gray-100);
}

.pv-check:last-child {
  border-bottom: 0;
}

.pv-check-done {
  color: var(--vp-success);
  font-size: 1.05rem;
}

.pv-check-todo {
  color: var(--vp-gray-400);
  font-size: 1.05rem;
}

.pv-check-label {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: var(--vp-gray-600);
}

/* Outstanding items read at full strength; done ones recede. */
.pv-check-label--todo {
  color: var(--vp-heading-color);
}

.pv-check-action {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--vp-accent);
  cursor: pointer;
}

.pv-check-action:hover {
  text-decoration: underline;
}
</style>
